import React from "react";
import SellingPropertyDetails from "./SellingPropertyDetails";
import SellingAddressDetails from "./SellingAddressDetails";
import SellingEstateAgent from "./SellingEstateAgent";
import BuyingPropertyDetails from "./BuyingPropertyDetails";
import BuyingAddressDetails from "./BuyingAddressDetails";
import BuyingEstateAgent from "./BuyingEstateAgent";
import SellingMortgagesAndLoans from "./SellingMortgagesAndLoans";
import BuyingMortgage from "./BuyingMortgage";
import BuyingMortgageLender from "./BuyingMortgageLender";
import YourName from "./YourName";
import ContactDetails from "./ContactDetails";
import OtherOwnersBuyingOrSelling from "./OtherOwnersBuyingOrSelling";
import AnythingElse from "./AnythingElse";
import SorryBuyingMortgageLender from "./SorryBuyingMortgageLender";
import BuyingSorryPage from "./BuyingSorryPage";
import SellingSorryPage from "./SellingSorryPage";
import TermsAndConditions from "./TermsAndConditions";
import SaleOwnersBuyingAndSelling from "./SaleOwnersBuyingAndSelling";
import PurchaseOwnersBuyingAndSelling from "./PurchaseOwnersBuyingAndSelling";
import BuyingMortgageBroker from "./BuyingMortgageBroker";
import BuyingMoneySources from "./BuyingMoneySources";
import BuyingMoneySorryPage from "./BuyingMoneySorryPage";
import HighRiskCountrySorryPage from "./HighRiskCountrySorryPage";
import { isHighRiskCountry } from "../../utils/countryRiskUtils";
import { formatMoneyWithCurrency } from "../../shared/utils/moneyUtils";
import { isEstateAgentReferral } from "./leadSourceReferrer";

const isSelling = (transactionType) => {
  return transactionType === "Selling" || transactionType === "BuyingAndSelling";
};

const isBuying = (transactionType) => {
  return transactionType === "Buying" || transactionType === "BuyingAndSelling";
};

const isBuyingAndSelling = (transactionType) => {
  return transactionType === "BuyingAndSelling";
};

const isBuyingWithOfferAccepted = (flowState) => {
  return isBuying(flowState.transactionType) && flowState.buyingPropertyOfferAccepted === true;
};

const shouldShowBuyingMortgageLender = (flowState) => {
  if (flowState.transactionType === "Buying" || flowState.transactionType === "BuyingAndSelling") {
    return flowState.buyingMortgage.buyerHasMortgage === true;
  }
};

const shouldShowBuyingMortgageBroker = (flowState) => {
  if (flowState.transactionType === "Buying" || flowState.transactionType === "BuyingAndSelling") {
    if (flowState.haveMortgageBrokerDetails) {
      return false;
    } else {
      return flowState.buyingMortgage.buyerHasMortgage === true;
    }
  }
};

const hasSellingRejection = (flowState) => {
  if (!flowState.sellingRejectionReason) return false;
  for (const value of flowState.sellingRejectionReason.values()) {
    if (value) return true;
  }
  return false;
};

const hasBuyingRejection = (flowState) => {
  if (!flowState.buyingRejectionReason) return false;
  for (const value of flowState.buyingRejectionReason.values()) {
    if (value) return true;
  }
  return false;
};

const hasBuyingMoneyRejection = (flowState) => {
  if (!flowState.purchaseMoneySources) return false;
  return flowState.purchaseMoneySources.highRiskCountry === true || flowState.purchaseMoneySources.usingCrypto === true;
};

const shouldShowSorryBuyingMortgageLender = (flowState, isBuyToLet) => {
  if (flowState.transactionType === "Buying" || flowState.transactionType === "BuyingAndSelling") {
    return (
      flowState.buyingMortgageLender &&
      flowState.buyingMortgageLender.buyingMortgageLender === "Other" &&
      isBuyToLet !== true
    );
  }
};

// An estate agent referral's agent goes on the sale's estate agent step. A
// purchase's step asks for the seller's agent, which the referrer often isn't.
const prefilledSellingEstateAgent = (request) => {
  if (!isSelling(request.transactionType) || !isEstateAgentReferral(request.leadSource)) return {};
  if (request.agentName == null && request.agentEmailAddress == null) return {};
  return {
    sellingEstateAgent: {
      hasEstateAgent: true,
      estateAgent: {
        name: request.agentName ?? "",
        emailAddress: request.agentEmailAddress ?? "",
        phoneNumber: "",
      },
      buyerFullName: "",
      buyerEmailAddress: "",
      buyerPhoneNumber: "",
    },
  };
};

// The flow state a sign-up starts from: the signup request, plus its quote's
// prices and, on a sale, its referring estate agent when it has a client-app
// quote. Every answer stays editable.
export const initialSignupFlowState = (request) => ({
  transactionType: request.transactionType,
  leadSource: request.leadSource,
  clientIsCompany: request.clientIsCompany,
  yourName: {
    client1: {
      companyName: "",
      firstName: request.firstName,
      lastName: request.lastName,
      emailAddress: request.email,
    },
  },
  contactDetails: {
    emailAddress: request.email,
    phoneNumber: request.phoneNumber,
    clientAddressIsSaleAddress: null,
    clientAddress: {
      addressLine1: "",
      addressLine2: "",
      addressLine3: "",
      addressTown: "",
      addressCounty: "",
      addressPostcode: "",
      addressCountryCode: "GBR",
    },
  },
  haveMortgageBrokerDetails: request.haveMortgageBrokerDetails,
  // Only for the sides the sign-up covers, in case the quote's tenures differ.
  ...(isSelling(request.transactionType) &&
    request.salePrice != null && { sellingAddressDetails: { price: formatMoneyWithCurrency(request.salePrice) } }),
  ...(isBuying(request.transactionType) &&
    request.purchasePrice != null && {
      buyingAddressDetails: { price: formatMoneyWithCurrency(request.purchasePrice) },
    }),
  ...prefilledSellingEstateAgent(request),
});

// The sign-up steps up to and including the terms and conditions, so another
// flow can share them. Each flow adds its own final steps after these.
//
// isBuyToLet comes from the signup request, and decides whether an unlisted
// mortgage lender ends the flow.
export const buildSignupSteps = ({ isBuyToLet, finalSteps }) => [
  {
    name: "Your name",
    slug: "your-name",
    component: (flowState) => (
      <YourName
        transactionType={flowState.transactionType}
        yourName={flowState.yourName}
        clientIsCompany={flowState.clientIsCompany}
      />
    ),
  },
  {
    name: "Contact details",
    slug: "contact-details",
    component: (flowState) => (
      <ContactDetails
        transactionType={flowState.transactionType}
        clientIsCompany={flowState.clientIsCompany}
        sellingAddressDetails={flowState.sellingAddressDetails}
        contactDetails={flowState.contactDetails}
      />
    ),
  },
  {
    name: "Sorry territories",
    slug: "sorry-territories",
    valid: (flowState) => isHighRiskCountry(flowState.contactDetails.clientAddress.addressCountryCode),
    component: (flowState) => (
      <HighRiskCountrySorryPage
        flowId={flowState.leadId}
        countryCode={flowState.contactDetails.clientAddress.addressCountryCode}
        transactionType={flowState.transactionType}
      />
    ),
  },
  {
    name: "Other owners",
    slug: "other-owners",
    valid: (flowState) =>
      !flowState.clientIsCompany && (flowState.transactionType === "Selling" || flowState.transactionType === "Buying"),
    component: (flowState) => (
      <OtherOwnersBuyingOrSelling transactionType={flowState.transactionType} otherOwners={flowState.otherOwners} />
    ),
  },
  {
    name: "Sale details",
    slug: "sale-details",
    valid: (flowState) => isSelling(flowState.transactionType),
    component: (flowState) => (
      <SellingPropertyDetails
        sellingPropertyOfferAccepted={flowState.sellingPropertyOfferAccepted}
        sellingRejectionReason={flowState.sellingRejectionReason}
      />
    ),
  },
  {
    name: "Selling sorry page",
    slug: "selling-sorry-page",
    valid: (flowState) => hasSellingRejection(flowState),
    component: (flowState) => (
      <SellingSorryPage
        transactionType={flowState.transactionType}
        sellingRejectionReason={flowState.sellingRejectionReason}
        leadId={flowState.leadId}
      />
    ),
  },
  {
    name: "Sale address",
    slug: "sale-address",
    valid: (flowState) => isSelling(flowState.transactionType),
    component: (flowState) => (
      <SellingAddressDetails
        propertyOfferAccepted={flowState.sellingPropertyOfferAccepted}
        sellingAddressDetails={flowState.sellingAddressDetails}
      />
    ),
  },
  {
    name: "Buying and selling who's selling",
    slug: "sale-owners-buying-and-selling",
    valid: (flowState) => isBuyingAndSelling(flowState.transactionType),
    component: (flowState) => (
      <SaleOwnersBuyingAndSelling
        transactionType={flowState.transactionType}
        otherOwnersSelling={flowState.otherOwnersSelling}
      />
    ),
  },
  {
    name: "Sale estate agent",
    slug: "sale-estate-agent",
    valid: (flowState) => isSelling(flowState.transactionType),
    component: (flowState) => (
      <SellingEstateAgent
        propertyOfferAccepted={flowState.sellingPropertyOfferAccepted}
        sellingEstateAgent={flowState.sellingEstateAgent}
      />
    ),
  },
  {
    name: "Sale mortgage and loans",
    slug: "sale-mortgages-and-loans",
    valid: (flowState) => isSelling(flowState.transactionType),
    component: (flowState) => (
      <SellingMortgagesAndLoans sellingMortgagesAndLoans={flowState.sellingMortgagesAndLoans} />
    ),
  },
  {
    name: "Buying and selling who's buying",
    slug: "purchase-owners-buying-and-selling",
    valid: (flowState) => isBuyingAndSelling(flowState.transactionType),
    component: (flowState) => (
      <PurchaseOwnersBuyingAndSelling
        transactionType={flowState.transactionType}
        otherOwnersSelling={flowState.otherOwnersSelling}
        otherOwnersPurchasing={flowState.otherOwnersPurchasing}
      />
    ),
  },
  {
    name: "Purchase details",
    slug: "purchase-details",
    valid: (flowState) => isBuying(flowState.transactionType),
    component: (flowState) => (
      <BuyingPropertyDetails
        transactionType={flowState.transactionType}
        clientIsCompany={flowState.clientIsCompany}
        buyingPropertyOfferAccepted={flowState.buyingPropertyOfferAccepted}
        buyingRejectionReason={flowState.buyingRejectionReason}
      />
    ),
  },
  {
    name: "Purchase sorry page",
    slug: "purchase-sorry-page",
    valid: (flowState) => hasBuyingRejection(flowState),
    component: (flowState) => (
      <BuyingSorryPage
        transactionType={flowState.transactionType}
        buyingRejectionReason={flowState.buyingRejectionReason}
        leadId={flowState.leadId}
      />
    ),
  },
  {
    name: "Purchase address",
    slug: "purchase-address",
    valid: (flowState) => isBuyingWithOfferAccepted(flowState),
    component: (flowState) => (
      <BuyingAddressDetails
        clientIsCompany={flowState.clientIsCompany}
        buyingAddressDetails={flowState.buyingAddressDetails}
      />
    ),
  },
  {
    name: "Purchase estate agent",
    slug: "purchase-estate-agent",
    valid: (flowState) => isBuyingWithOfferAccepted(flowState),
    component: (flowState) => <BuyingEstateAgent buyingEstateAgent={flowState.buyingEstateAgent} />,
  },
  {
    name: "Purchase money sources",
    slug: "purchase-money-sources",
    valid: (flowState) => isBuying(flowState.transactionType),
    component: (flowState) => <BuyingMoneySources purchaseMoneySources={flowState.purchaseMoneySources} />,
  },
  {
    name: "Purchase money sorry page",
    slug: "purchase-money-sorry-page",
    valid: (flowState) => hasBuyingMoneyRejection(flowState),
    component: (flowState) => (
      <BuyingMoneySorryPage
        transactionType={flowState.transactionType}
        purchaseMoneySources={flowState.purchaseMoneySources}
        leadId={flowState.leadId}
      />
    ),
  },
  {
    name: "Purchase mortgage",
    slug: "purchase-mortgage",
    valid: (flowState) => isBuying(flowState.transactionType),
    component: (flowState) => (
      <BuyingMortgage clientIsCompany={flowState.clientIsCompany} buyingMortgage={flowState.buyingMortgage} />
    ),
  },
  {
    name: "Purchase mortgage lender",
    slug: "purchase-mortgage-lender",
    valid: (flowState) => shouldShowBuyingMortgageLender(flowState),
    component: (flowState) => <BuyingMortgageLender buyingMortgageLender={flowState.buyingMortgageLender} />,
  },
  {
    name: "Sorry mortgage lender",
    slug: "sorry-mortgage-lender",
    valid: (flowState) => shouldShowSorryBuyingMortgageLender(flowState, isBuyToLet),
    component: (flowState) => <SorryBuyingMortgageLender leadId={flowState.leadId} />,
  },
  {
    name: "Purchase mortgage broker",
    slug: "purchase-mortgage-broker",
    valid: (flowState) => shouldShowBuyingMortgageBroker(flowState),
    component: (flowState) => <BuyingMortgageBroker mortgageBroker={flowState.mortgageBroker} />,
  },
  {
    name: "Anything else",
    slug: "anything-else",
    component: (flowState) => (
      <AnythingElse
        transactionType={flowState.transactionType}
        anythingElse={flowState.anythingElse}
        leadSource={flowState.leadSource}
      />
    ),
  },
  {
    name: "Terms and conditions",
    slug: "terms-and-conditions",
    component: (flowState) => (
      <TermsAndConditions
        transactionType={flowState.transactionType}
        clientIsCompany={flowState.clientIsCompany}
        termsAndConditions={flowState.termsAndConditions}
        emailAddress={flowState.contactDetails.emailAddress}
      />
    ),
  },
  ...finalSteps,
];
